import { useAppSelector, useAppDispatch } from 'src/store';
import { getIntegrationCatalog } from 'src/components/Integrations/IntegrationTypes';
import AddZendeskIcon from 'src/assets/img/add-integration/add-integration-zendesk-icon.svg';
import AddZohoChatIcon from 'src/assets/img/add-integration/add-integration-zoho-desk-black.svg';
import AddZohoEmailIcon from 'src/assets/img/add-integration/add-integration-zoho-email-black.svg';
import AddZendeskChatIconBlack from 'src/assets/img/add-integration/add-integration-zendesk-chat-black.svg';
import AddZendeskEmailIcon from 'src/assets/img/add-integration/add-integration-zendesk-email-icon.svg';
import AddFreshdeskIcon from 'src/assets/img/add-integration/add-integration-freshdesk-icon.svg';
import AddFreshdeskChatIcon from 'src/assets/img/add-integration/add-integration-freshdesk-chat-black.svg';
import AddFreshdeskEmailIcon from 'src/assets/img/add-integration/add-integration-freshdesk-email-black.svg';
import AddSlackIcon from 'src/assets/img/add-integration/add-integration-slack-icon.svg';
import { useTheme } from 'src/provider/ThemeProvider';
import { clsx } from 'clsx';
import { IntegrationItem } from './IntegrationItem';
import { MessageIcon } from 'src/assets/icons/MessageIcon';
import { MailIcon } from 'src/assets/icons/MailIcon';
import { COSUPPORT_URL } from 'src/constants/urls';
import { Button } from 'src/components/ui/button';
import { useGetProjectByIdQuery } from 'src/services';
import { useParams } from 'react-router-dom';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationCatalogEntry } from 'src/components/Integrations/IntegrationTypes';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import type { Project } from 'src/services/projectsService';

interface IntegrationModalStep1Props {
  project?: Project;
  createIntegration: IntegrationSubmitHandler;
  // Picks an integration type to set up in the next step.
  onClick: (integrationType: IntegrationCatalogEntry) => void;
  close?: () => void;
}

export const IntegrationModalStep1 = ({
  project,
  createIntegration,
  onClick,
  close = () => {},
}: IntegrationModalStep1Props) => {
  const { id } = useParams();
  const { config } = useTheme();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const dispatch = useAppDispatch();

  const integrationTypes = getIntegrationCatalog(projectType);

  const createZohoSalesIqIntegration = (integrationType: IntegrationCatalogEntry) => {
    dispatch(setSelectedIntegration(integrationType));
    createIntegration({
      integration_type: integrationType.data.integration_type,
      integration_data: {
        project_name: currentProject.name,
      },
    });
  };

  return (
    <div className="content">
      <ul className={clsx('grid grid-cols-2 gap-3', config.className)}>
        {integrationTypes?.map((integrationType) => {
          const type = integrationType.data.integration_type;
          const isExist = project?.integrations?.find(
            (integration) => integration.integration_type === type,
          );

          if (isExist) {
            return null;
          }

          switch (type) {
            case 'zendesk':
              return (
                <IntegrationItem
                  key={type}
                  icon={AddZendeskIcon}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'zoho_salesiq':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MessageIcon]}
                  icon={AddZohoChatIcon}
                  name={integrationType.friendlyName}
                  onClick={() => createZohoSalesIqIntegration(integrationType)}
                />
              );
            case 'zoho_desk':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MailIcon]}
                  icon={AddZohoEmailIcon}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'zendesk_chat':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MessageIcon]}
                  icon={AddZendeskChatIconBlack}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'zendesk_email':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MailIcon]}
                  icon={AddZendeskEmailIcon}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'freshdesk':
              return (
                <IntegrationItem
                  key={type}
                  icon={AddFreshdeskIcon}
                  icons={[MailIcon]}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'freshdesk_chat':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MessageIcon]}
                  icon={AddFreshdeskChatIcon}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'freshdesk_email':
              return (
                <IntegrationItem
                  key={type}
                  icons={[MailIcon]}
                  icon={AddFreshdeskEmailIcon}
                  name={integrationType.friendlyName}
                  onClick={() => onClick(integrationType)}
                />
              );
            case 'slack': {
              const request = { ...integrationType };

              request.data.integration_data.project_name = project.name;
              return (
                <IntegrationItem
                  key={type}
                  icon={AddSlackIcon}
                  name={'Slack'}
                  onClick={() => onClick(request)}
                />
              );
            }
            default:
              return null;
          }
        })}
      </ul>
      <div
        className={
          'mt-5 py-4 px-2.5 bg-[#F5FCFE] border border-[#009cb240] text-center text-xs text-[#009CB2] font-medium'
        }
      >
        <p>Don't see your help desk listed?</p>
        <p>
          Request a custom integration by submitting the{' '}
          <a
            className={'font-semibold italic underline'}
            href={COSUPPORT_URL.REQUEST_DEMO}
            target="_blank"
            rel="noopener noreferrer"
          >
            Demo Form
          </a>
          {'.'}
        </p>
      </div>
      <div className="sticky bottom-0">
        <div className="flex w-full items-center justify-center gap-2.5 pb-6 pt-5">
          <Button
            type="button"
            variant="outline"
            className="transparent black min-w-28"
            onClick={() => close?.()}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};
